import { Link } from 'react-router-dom';
import { BOOST_PRICE_CENTS } from '@affily/shared/constants';
import { formatCents } from '@affily/shared/money';
import { Seo } from '../components/Seo.jsx';
import { Button, Badge, Alert } from '../components/ui.jsx';

/**
 * Affily Boost — stubbed on purpose.
 *
 * Visible and priced, but not wired to Stripe. The page is honest about that
 * rather than collecting a card and failing, and it is equally explicit about
 * the one thing Boost cannot do: touch organic ranking.
 */
export default function Boost() {
  return (
    <>
      <Seo
        title="Affily Boost — featured directory placement"
        description={`Featured placement in the Affily directory for ${formatCents(BOOST_PRICE_CENTS)}/mo. A labelled slot — it never reorders organic results.`}
      />

      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <Badge tone="accent">Affily Boost</Badge>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight">
          Featured placement, clearly labelled.
        </h1>
        <p className="mt-4 text-lg text-ink-muted">
          A slot at the top of the programs directory, marked as paid placement, for{' '}
          {formatCents(BOOST_PRICE_CENTS)} a month.
        </p>

        <Alert tone="caution" className="mt-6">
          Boost isn’t live yet — this page is a preview and there is nothing to buy. We’d rather
          show you the price now than take a card for something we haven’t built.
        </Alert>

        <div className="mt-10 rounded-lg border border-rule bg-paper-raised p-6">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-semibold tabular-nums">
              {formatCents(BOOST_PRICE_CENTS)}
            </span>
            <span className="text-ink-muted">/month per program</span>
          </div>

          <ul className="mt-6 space-y-3 text-sm">
            {[
              'A featured slot above the programs directory results',
              'Labelled “Featured · paid placement”, always',
              'Cancel any time; no contract',
            ].map((item) => (
              <li key={item} className="flex gap-2.5">
                <svg
                  viewBox="0 0 16 16"
                  className="mt-0.5 size-4 shrink-0 text-positive"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M3 8.5l3.2 3.2L13 5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {item}
              </li>
            ))}
          </ul>

          <Button disabled className="mt-6 w-full" size="lg">
            Not available yet
          </Button>
        </div>

        <section className="mt-10">
          <h2 className="text-xl font-semibold tracking-tight">What Boost does not do</h2>
          <p className="mt-3 text-ink-muted">
            It cannot move your program up the organic results. Not by a little, not subtly, not at
            all. Featured entries are returned by the API as a separate list and rendered in a
            separate block; the code that orders organic results never sees the boost flag, and
            there is a test asserting the organic ordering is byte-identical with a boost applied.
          </p>
          <p className="mt-3 text-ink-muted">
            It also cannot move a creator’s transparency score, hide a bad conversion rate, or take
            a program’s terms off its public page.
          </p>
          <p className="mt-3 text-ink-muted">
            If any of that were purchasable, the directory would stop being worth reading — which
            would cost us more than {formatCents(BOOST_PRICE_CENTS)} a month.
          </p>
          <Link
            to="/transparency"
            className="mt-4 inline-block text-sm font-medium text-accent hover:underline"
          >
            How transparency works →
          </Link>
        </section>
      </div>
    </>
  );
}
